import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { dbPromise } from '../indexdb';
import type { VideoStoreType, VideoType } from './types';

const FIFTEEN_DAYS = 15 * 24 * 60 * 60 * 1000;

async function getStateFromIndexedDB() {
  const db = await dbPromise;
  const videoClips = (await db.get('record-history', 'clips')) || {};
  return videoClips;
}

async function setStateToIndexedDB(state: any) {
  const db = await dbPromise;
  await db.put('record-history', state, 'clips');
}

async function removeStateFromIndexedDB() {
  const db = await dbPromise;
  await db.delete('record-history', 'clips');
}

export const useVideoStore = create(
  persist<VideoStoreType>(
    (set, get) => ({
      videos: {},
      addVideo: (workspaceId: string, userId: string, video: VideoType) =>
        set((state) => {
          const currentVideos = state.videos[workspaceId]?.[userId] || [];
          const updatedVideos = [...currentVideos, video].slice(-3);

          return {
            videos: {
              ...state.videos,
              [workspaceId]: {
                ...(state.videos[workspaceId] || {}),
                [userId]: updatedVideos,
              },
            },
          };
        }),
      getVideos: (workspaceId, userId) => {
        const clips = get().videos[workspaceId]?.[userId] || [];
        const threshold = Date.now() - FIFTEEN_DAYS;
        const validClips = clips.filter(
          (v) => new Date(v.createdAt).getTime() >= threshold,
        );

        return validClips;
      },
      removeVideo: (workspaceId: string, userId: string, videoDate: string) =>
        set((state) => {
          const currentVideos = state.videos[workspaceId]?.[userId] || [];
          const updatedVideos = currentVideos.filter(
            (video) => video.createdAt !== videoDate,
          );

          return {
            videos: {
              ...state.videos,
              [workspaceId]: {
                ...(state.videos[workspaceId] || {}),
                [userId]: updatedVideos,
              },
            },
          };
        }),
    }),
    {
      name: 'video-store',
      getStorage: () => ({
        getItem: async (key) => {
          const state = await getStateFromIndexedDB();
          return JSON.stringify(state);
        },
        setItem: async (key, value) => {
          const state = JSON.parse(value);
          await setStateToIndexedDB(state);
        },
        removeItem: async (key) => {
          await removeStateFromIndexedDB();
        },
      }),
    },
  ),
);
